import { AnalyticsClick } from '@ovotech/ui-tools';

import React, { useMemo } from 'react';
import NavDropdown from '@/src/components/Navigation/Links/NavDropdown';
import NavLink from '@/src/components/Navigation/Links/NavLink';
import { useDecision } from '@/src/utils/optimizely';

import { ROUTE_REWARDS } from '@/src/constants/routes';
import { NewNavItemBadge } from '../../Badges/NewNavItemBadge';
import { BEYOND_WEB_PRESENCE } from '@/src/constants/feature-flags';
import { MENU, BeyondStatusValue } from '@/src/constants/analytics';
import { useBeyondAccountFlags } from '@/src/hooks/useBeyondAccountFlags/useBeyondAccountFlags';

type DropdownLinksParams = {
  path: string;
  beyondStatus: BeyondStatusValue | undefined;
};

const useDropdownLinks = ({ path, beyondStatus }: DropdownLinksParams) => {
  const [beyondWebPresence] = useDecision(BEYOND_WEB_PRESENCE);

  const label =
    beyondWebPresence?.variationKey === 'rewards' ? 'All rewards' : 'Rewards';
  const testVariationKey =
    beyondWebPresence?.variationKey === 'rewards' ? 'Rewards' : 'Beyond';

  return useMemo(() => {
    const isActive = (route: string) => path.includes(route);
    const links = [
      <li key={ROUTE_REWARDS.ALL_REWARDS}>
        <AnalyticsClick
          name={MENU.ALL_REWARDS}
          properties={{
            'Test Variation': testVariationKey,
            ...(beyondStatus && { 'Beyond Status': beyondStatus }),
          }}
        >
          <NavLink
            path={path}
            route={ROUTE_REWARDS.ALL_REWARDS}
            label={label}
            badge={<NewNavItemBadge />}
            isActiveChild={isActive(ROUTE_REWARDS.ALL_REWARDS)}
          />
        </AnalyticsClick>
      </li>,
    ];

    return links;
  }, [path, label, testVariationKey, beyondStatus]);
};

type Props = {
  type: 'accordion' | 'dropdown';
  path: string;
};

const RewardsLink = ({ type, path }: Props) => {
  const [beyondWebPresence] = useDecision(BEYOND_WEB_PRESENCE);
  const { beyondStatus } = useBeyondAccountFlags();
  const dropdownLinks = useDropdownLinks({ path, beyondStatus });

  const testVariationKey =
    beyondWebPresence?.variationKey === 'rewards' ? 'Rewards' : 'Beyond';

  const label =
    beyondWebPresence?.variationKey === 'rewards' ? 'Rewards' : 'Beyond';

  return (
    <AnalyticsClick
      name={MENU.REWARDS_DROPDOWN}
      properties={{
        'Test variant': testVariationKey,
        ...(beyondStatus && { 'Beyond Status': beyondStatus }),
      }}
    >
      <NavDropdown
        label={label}
        listItems={dropdownLinks}
        accordion={type === 'accordion'}
        defaultOpen={true}
        showBadge
        badge={<NewNavItemBadge />}
      />
    </AnalyticsClick>
  );
};

export default RewardsLink;
